import { subjectSkillIds, getSkill, skillUrl } from "../data/study-skills"
import React from "react"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Grid from "@material-ui/core/Grid"
import Button from "@material-ui/core/Button"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"

import Image from "./image"

export interface StudySummaryRow {
  stage: string
  rhythm: string
  focus: string
  habit: string
}

export interface StudySection {
  title: string
  paragraphs: string[]
}

interface Props {
  subject: string
  title: string
  intro: string
  image: string
  imageAlt: string
  sharedCalendarUrl?: string
  summary: StudySummaryRow[]
  sections: StudySection[]
}

const sectionSpacing = {
  marginTop: 32,
}

const StudyRoutinePost = ({
  subject,
  title,
  intro,
  image,
  imageAlt,
  sharedCalendarUrl,
  summary,
  sections,
}: Props) => (
  <Box component="article" style={{ paddingTop: 40, paddingBottom: 56 }}>
    <Button
      color="primary"
      href="/guides/?group=Activity%20routines&category=Study"
      style={{ marginBottom: 24 }}
    >
      Back to study routines
    </Button>

    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={7}>
        <Typography variant="overline" color="primary">
          {subject} Study
        </Typography>
        <Typography variant="h3" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {intro}
        </Typography>
        {sharedCalendarUrl && (
          <Box style={{ marginTop: 20 }}>
            <Button
              variant="contained"
              color="primary"
              href={sharedCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the shared weekly calendar
            </Button>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={5}>
        <Image alt={imageAlt} filename={image} />
      </Grid>
    </Grid>

    <Box style={sectionSpacing}>
      <Typography variant="body1" paragraph>
        Use these routines as a planning guide alongside teacher advice,
        assignments, exams, and the student's energy after school. The best
        study plan is visible, repeatable, and specific enough to start without
        negotiating every afternoon.
      </Typography>
    </Box>

    <Box style={sectionSpacing}>
      <Typography variant="h5" component="h2" gutterBottom>
        Quick study summary
      </Typography>
      <Box style={{ overflowX: "auto" }}>
        <Table aria-label={`${subject} study routine summary`}>
          <TableHead>
            <TableRow>
              <TableCell>Stage</TableCell>
              <TableCell>Weekly rhythm</TableCell>
              <TableCell>Main focus</TableCell>
              <TableCell>Useful habit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.map(row => (
              <TableRow key={row.stage}>
                <TableCell component="th" scope="row">
                  {row.stage}
                </TableCell>
                <TableCell>{row.rhythm}</TableCell>
                <TableCell>{row.focus}</TableCell>
                <TableCell>{row.habit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>

    {sections.map(section => (
      <Box style={sectionSpacing} key={section.title}>
        <Typography variant="h5" component="h2" gutterBottom>
          {section.title}
        </Typography>
        {section.paragraphs.map(paragraph => (
          <Typography variant="body1" paragraph key={paragraph}>
            {paragraph}
          </Typography>
        ))}
      </Box>
    ))}
    <Box style={sectionSpacing}>
      <Typography variant="h5" component="h2" gutterBottom>
        Choose how to study
      </Typography>
      <Typography paragraph>
        Pair this subject plan with practical methods from the{" "}
        <a href="/study-skills/">study skills guide</a>.
      </Typography>
      <ul>
        {(subjectSkillIds[subject] || []).map(id => (
          <li key={id}>
            <a href={skillUrl(getSkill(id))}>{getSkill(id).title}</a>
          </li>
        ))}
      </ul>
    </Box>
  </Box>
)

export default StudyRoutinePost
